"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, FlaskConical, ShieldCheck, Activity, ArrowRight, Sparkles, Layers, FileCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface ApplicationItem {
  id: string;
  title: string;
  tagline: string;
  description: string;
  badge: string;
  link: string;
  features: string[];
}
const applicationItems: ApplicationItem[] = [{
  id: "ingredient-verification",
  title: "Ingredient Verification",
  tagline: "Raw Material Identity & Purity",
  description: "Ensure incoming bulk materials, active substances, and excipients meet required monographs with high-precision spectroscopy.",
  badge: "Identity Testing",
  link: "/RawMaterialIdentificationPage",
  features: ["Incoming container verification and release", "Direct barrier & bag identification", "Electronic data records and audit trails"]
}, {
  id: "formulation-testing",
  title: "Formulation Testing",
  tagline: "Blend Uniformity & Matrix Analysis",
  description: "Monitor physical and chemical characteristics during development and scale-up to ensure batch consistency.",
  badge: "Process Analytics",
  link: "/BlendUniformityPage",
  features: ["Real-time blend uniformity detection", "Tablet hardness & dissolution checks", "Powder flow & density evaluation"]
}, {
  id: "safety-compliance",
  title: "Safety Compliance",
  tagline: "Operator Protection & Contaminant Screening",
  description: "Mitigate laboratory exposure risks, verify chemical handling protocols, and satisfy stringent MHRA and HPRA regulations.",
  badge: "Regulated Standards",
  link: "/SafetyAndSecurityPage",
  features: ["Non-destructive sample inspection", "Trace contaminant screening", "Audit-ready data traceability"]
}, {
  id: "quality-control",
  title: "Quality Control",
  tagline: "Batch Release & In-Line PAT",
  description: "Streamline final product sign-off with automated analytical instruments configured for high-throughput testing cycles.",
  badge: "Continuous Verification",
  link: "/PowderTestingPage",
  features: ["Immediate endpoint determination", "Solvent recovery monitoring", "Reduced quarantine lead times"]
}];
export default function ApplicationAreas() {
  const prefersReducedMotion = useReducedMotion();
  const getIcon = (id: string) => {
    switch (id) {
      case "ingredient-verification":
        return <FlaskConical className="size-6 text-primary" />;
      case "formulation-testing":
        return <Layers className="size-6 text-primary" />;
      case "safety-compliance":
        return <ShieldCheck className="size-6 text-primary" />;
      case "quality-control":
        return <Activity className="size-6 text-primary" />;
      default:
        return <CheckCircle2 className="size-6 text-primary" />;
    }
  };
  return <section id="applicationareas" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="page-container">
        {/* Header Block */}
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mx-auto max-w-2xl text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 font-medium">
              <Sparkles className="size-3.5 text-primary" />
              Targeted Analytical Applications
            </Badge>
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Specialized Testing Across the Product Lifecycle
          </h2>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed">
            Reliable instruments and qualification workflows built specifically for pharmaceutical laboratories, manufacturing suites, and regulated quality teams.
          </p>
        </motion.div>

        {/* Application Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {applicationItems.map((item, index) => <motion.div key={item.id} data-index={index} initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: prefersReducedMotion ? 0 : index * 0.1,
          ease: "easeOut"
        }} className="h-full">
              <Card className="h-full flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40 rounded-xl overflow-hidden p-6 sm:p-8">
                <div>
                  <CardHeader className="p-0 mb-5">
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <div className="size-12 rounded-lg bg-secondary flex items-center justify-center shrink-0 border border-border">
                        {getIcon(item.id)}
                      </div>
                      <Badge variant="outline" className="text-xs font-normal text-muted-foreground bg-muted/50">
                        {item.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-bold text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <p className="text-xs font-semibold text-primary uppercase tracking-wider mt-1">
                      {item.tagline}
                    </p>
                  </CardHeader>

                  <CardContent className="p-0">
                    <CardDescription className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                      {item.description}
                    </CardDescription>

                    <div className="pt-4 border-t border-border/80">
                      <p className="text-xs font-medium text-foreground mb-3 flex items-center gap-1.5">
                        <FileCheck className="size-3.5 text-primary" />
                        Key Capabilities
                      </p>
                      <ul className="space-y-2">
                        {item.features.map((feature, featureIndex) => <li key={featureIndex} data-index={featureIndex} className="flex items-start text-sm text-muted-foreground gap-2.5">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </div>

                <div className="mt-8 pt-4 border-t border-border/60 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Regulated Environment Support</span>
                  <Button variant="ghost" size="sm" asChild className="group text-foreground hover:text-primary">
                    <Link to={item.link} className="inline-flex items-center gap-1.5 font-medium">
                      <span>Explore application</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </Button>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Section Footer Callout */}
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1
      } : {
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-12 md:mt-16 rounded-xl bg-secondary p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 border border-border">
          <div className="text-center sm:text-left">
            <h3 className="text-lg font-semibold text-secondary-foreground">
              Need custom testing methods or qualification protocols?
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Our application specialists provide on-site validation and method support across Ireland and the UK.
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-3">
            <Button asChild>
              <Link to="/ContactPage">Consult a Specialist</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}